import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, uploadFiles } from "@/lib/api";
import { SignedImg } from "@/components/shared/SignedImg";
import { LoadMore } from "@/components/shared/LoadMore";
import { PhotoPinViewer, photoInfo } from "@/components/defects/PhotoPinViewer";
import { FilterChip } from "@/components/shared/PagePrimitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDate, plur } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Camera, Trash2, Pencil, Check, X, ImagePlus } from "lucide-react";
import { usePhotoInputs } from "@/components/shared/PhotoPicker";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";

// Banka fotek zakázky. Nic se sem nekopíruje — je to pohled na tytéž fotky,
// které chodí z docházky, denního výkazu a splněných úkolů. Každá nese
// zakázku, datum a autora, takže se ve stejný den objeví i v deníku.

const MONTHS = ["leden", "únor", "březen", "duben", "květen", "červen",
                "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
const monthKey = (d) => (d || "").slice(0, 7);
const monthLabel = (k) => {
  const [y, m] = k.split("-");
  return `${MONTHS[Number(m) - 1] || m} ${y}`;
};
// 216: fotobanka po stránkách (dřív až 5 000 fotek najednou).
const PAGE = 60;

// `headless` = hlavičku (název, počet, tlačítko) vykresluje rodič. Používá to
// karta Dokumenty, aby hlavní tlačítko bylo pořád na stejném místě a nepře-
// skakovalo při přepnutí podzáložky. `onApi` posílá rodiči, jak spustit výběr
// fotek a v jakém je nahrávání stavu.
export function ProjectPhotosTab({ projectId, canEdit, embedded, headless = false, onApi }) {
  const { user } = useAuth();
  const canDelete = ["OWNER", "ASSISTANT"].includes(user?.role);  // MANAGER už fotky nemaže (zadání 115 §3)
  const [data, setData] = useState(null);
  const [source, setSource] = useState("all");
  const [authorId, setAuthorId] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [caption, setCaption] = useState(null);   // { id, text }
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState(null);         // { done, total } při nahrávání
  const [loadingMore, setLoadingMore] = useState(false);
  const reqRef = useRef(0);
  const photoPick = usePhotoInputs({ onChange: (e) => pickFiles(e), testId: "photos-input" });
  // Přetažení myší. Čítač místo boolean: dragleave chodí při každém přejezdu
  // přes vnitřní prvek karty — s pouhým true/false by overlay blikal.
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);

  // Rodič spouští nahrávání ze své hlavičky a potřebuje znát počet fotek.
  // `pick` = výběr souboru (desktop / Z galerie), `pickCamera` = foťák (mobil).
  useEffect(() => {
    onApi?.({ pick: photoPick.open, pickCamera: photoPick.openCamera, total: data?.total || 0, busy, progress: prog });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onApi, data, busy, prog]);

  const params = useCallback((extra) => {
    const p = new URLSearchParams();
    if (source !== "all") p.set("source", source);
    if (authorId !== "all") p.set("author_id", authorId);
    Object.entries(extra).forEach(([k, v]) => p.set(k, String(v)));
    return p.toString();
  }, [source, authorId]);
  // `keep` = kolik fotek už je načteno — obnova po akci vrátí stejně dlouhý seznam.
  const load = useCallback(async (keep = 0) => {
    const id = ++reqRef.current;
    try {
      const { data: d } = await api.get(`/projects/${projectId}/photos?${params({ limit: Math.min(500, Math.max(PAGE, keep)) })}`);
      if (id === reqRef.current) setData(d);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [projectId, params]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => load(data?.photos?.length || 0);

  const loadMore = useCallback(async () => {
    if (!data?.next_cursor || loadingMore) return;
    const id = reqRef.current;
    setLoadingMore(true);
    try {
      const { data: d } = await api.get(`/projects/${projectId}/photos?${params({ limit: PAGE, cursor: data.next_cursor })}`);
      if (id !== reqRef.current) return;
      setData((cur) => {
        const have = new Set((cur?.photos || []).map((p) => p.id));
        return { ...d, photos: [...(cur?.photos || []), ...d.photos.filter((p) => !have.has(p.id))] };
      });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoadingMore(false); }
  }, [projectId, params, data, loadingMore]);

  // Jeden vstup pro všechny tři cesty: dialog výběru, přetažení myší
  // (třeba rovnou z aplikace Fotky) a Cmd+V ze schránky. Přetažení obchází
  // i macOS oprávnění k Fotkám — prohlížeč dostane hotový soubor.
  const addFiles = useCallback(async (list) => {
    const all = [...(list || [])];
    const files = all.filter((f) => f?.type?.startsWith("image/") || /\.(heic|heif)$/i.test(f?.name || ""));
    if (!files.length) {
      if (all.length) toast.error("Do fotobanky patří jen fotky — dokumenty nahraj do vedlejší záložky.");
      return;
    }
    if (files.length < all.length) toast.error("Soubory, které nejsou fotky, jsem přeskočil.");
    setBusy(true);
    setProg({ done: 0, total: files.length });
    try {
      // 216: tři fotky naráz; jedna nepovedená nezahodí ostatní.
      const { urls, failed } = await uploadFiles(files, { onEach: (done, total) => setProg({ done, total }) });
      if (urls.length) await api.post(`/projects/${projectId}/photos`, { urls });
      if (!failed.length) toast.success(`Přidáno ${plur(urls.length, "fotka", "fotky", "fotek")}.`);
      else toast.error(`Nahráno ${urls.length} z ${files.length}. Nepovedlo se: ${failed.map((x) => x.file?.name || "fotka").join(", ")} — ${formatApiError(failed[0].error?.response?.data?.detail)}`, { duration: 12000 });
      if (urls.length) load(data?.photos?.length || 0);
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); setProg(null); }
  }, [projectId, load, data]);
  const pickFiles = (e) => {
    const list = [...(e.target.files || [])];
    e.target.value = "";
    addFiles(list);
  };

  // Cmd+V: screenshot nebo fotka zkopírovaná ve Fotkách rovnou do banky.
  useEffect(() => {
    if (!canEdit) return;
    const onPaste = (e) => {
      const files = [...(e.clipboardData?.files || [])];
      if (files.length) { e.preventDefault(); addFiles(files); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [canEdit, addFiles]);

  const saveCaption = async () => {
    try {
      await api.patch(`/photos/${caption.id}`, { caption: caption.text });
      // 216: jen přepsat popisek u načtené fotky — bez znovunačtení celé banky.
      const { id, text } = caption;
      setData((d) => ({ ...d, photos: d.photos.map((p) => (p.id === id ? { ...p, caption: text.trim() } : p)) }));
      setCaption(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!await ask("Smazat fotku? Zmizí i z deníku toho dne.")) return;
    try { await api.delete(`/photos/${p.id}`); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const photos = useMemo(() => data?.photos || [], [data]);
  const indexOf = useMemo(() => new Map(photos.map((p, i) => [p.id, i])), [photos]);
  // Počet v hlavičce měsíce je za celý měsíc ze serveru, ne jen za načtenou část.
  const monthCount = useMemo(() => new Map((data?.months || []).map((m) => [m.key, m.count])), [data]);

  if (!data) return <p className="text-sm text-muted-foreground">Načítám fotky…</p>;

  const months = [];
  photos.forEach((p) => {
    const k = monthKey(p.date);
    const last = months[months.length - 1];
    if (last && last.key === k) last.items.push(p);
    else months.push({ key: k, items: [p] });
  });

  return (
    <div className={`relative ${embedded ? "" : "bg-card border border-border rounded-[16px] p-4 md:p-5"}`} data-testid="project-photos-tab"
      onDragEnter={canEdit ? (e) => { e.preventDefault(); dragDepth.current += 1; setDragOver(true); } : undefined}
      onDragOver={canEdit ? (e) => e.preventDefault() : undefined}
      onDragLeave={canEdit ? () => { dragDepth.current -= 1; if (dragDepth.current <= 0) { dragDepth.current = 0; setDragOver(false); } } : undefined}
      onDrop={canEdit ? (e) => { e.preventDefault(); dragDepth.current = 0; setDragOver(false); addFiles(e.dataTransfer?.files); } : undefined}>
      {dragOver && (
        <div className="absolute inset-0 z-10 rounded-[16px] border-2 border-dashed border-brand bg-brand/10 grid place-items-center pointer-events-none" data-testid="photos-dropzone">
          <div className="flex flex-col items-center gap-2 text-brand font-bold text-center px-4">
            <ImagePlus size={32} /> Pusťte fotky sem
          </div>
        </div>
      )}
      {/* Skryté vstupy musí zůstat i v režimu headless — tlačítka v hlavičce
          rodiče přes ně otevírají foťák / výběr souborů. */}
      {canEdit && photoPick.inputs}
      {!headless && (
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <h2 className="text-[19px] font-black tracking-tight flex items-center gap-2">
            <Camera size={18} className="text-brand" /> Fotky
          </h2>
          <span className="text-sm text-muted-foreground" data-testid="photos-total">
            {plur(data.total, "fotka", "fotky", "fotek")}
          </span>
          <div className="flex-1" />
          {canEdit && (
            <>
              {/* Jedno tlačítko: iOS nativní nabídka, Android Vyfotit / Galerie / Soubor, PC výběr souboru. */}
              <Button onClick={photoPick.open} disabled={busy}
                className="h-[42px] w-full md:w-auto font-bold" data-testid="photos-upload-btn">
                <Camera size={16} /> {busy ? (prog ? `Nahrávám ${prog.done}/${prog.total}…` : "Nahrávám…") : "Nahrát fotky"}
              </Button>
            </>
          )}
        </div>
      )}

      {/* Filtry: zdroj a autor. Počty jsou z celé zakázky, ne z výběru. */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <FilterChip label="Vše" active={source === "all"} onClick={() => setSource("all")} testid="photos-src-all" />
        {(data.sources || []).map((s) => (
          <FilterChip key={s.value} label={`${s.label} (${s.count})`} active={source === s.value}
            onClick={() => setSource(s.value)} testid={`photos-src-${s.value}`} />
        ))}
        <div className="flex-1 min-w-0" />
        <Select value={authorId} onValueChange={setAuthorId}>
          <SelectTrigger className="h-10 w-full md:w-[220px]" data-testid="photos-author"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Všichni autoři</SelectItem>
            {(data.authors || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!photos.length && (
        <p className="text-sm text-muted-foreground" data-testid="photos-empty">
          Zatím žádné fotky. Objeví se tu samy, jakmile někdo přidá fotku při odchodu ze směny nebo u splněného úkolu.
          {canEdit ? " Můžeš je sem taky přetáhnout myší — třeba rovnou z aplikace Fotky — nebo vložit přes Cmd+V." : ""}
        </p>
      )}

      {months.map((m) => (
        <div key={m.key} className="mb-6 last:mb-0" data-testid={`photos-month-${m.key}`}>
          <div className="flex items-center gap-3 mb-2.5">
            <h3 className="font-heading font-extrabold text-[13px] uppercase tracking-[.07em]">{monthLabel(m.key)}</h3>
            <span className="text-xs text-muted-foreground">{plur(monthCount.get(m.key) ?? m.items.length, "fotka", "fotky", "fotek")}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {m.items.map((p) => (
              <div key={p.id} className="group border border-border rounded-[12px] overflow-hidden bg-secondary/40" data-testid={`photo-${p.id}`}>
                <button type="button" onClick={() => setLightbox(indexOf.get(p.id) ?? 0)} aria-label="Zvětšit fotku"
                  className="block w-full aspect-square overflow-hidden">
                  <SignedImg url={p.url} alt="" thumb={240} className="w-full h-full object-cover" />
                </button>
                <div className="p-2.5 min-w-0">
                  {caption?.id === p.id ? (
                    <div className="flex items-center gap-1">
                      <Input value={caption.text} onChange={(e) => setCaption({ ...caption, text: e.target.value })}
                        onKeyDown={(e) => { if (e.key === "Enter") saveCaption(); }} autoFocus
                        className="h-9 text-sm min-w-0" data-testid={`photo-caption-input-${p.id}`} />
                      <button type="button" onClick={saveCaption} className="w-9 h-9 grid place-items-center text-success shrink-0" data-testid={`photo-caption-save-${p.id}`}><Check size={16} /></button>
                      <button type="button" onClick={() => setCaption(null)} className="w-9 h-9 grid place-items-center text-muted-foreground shrink-0"><X size={16} /></button>
                    </div>
                  ) : (
                    <>
                      {p.caption && <div className="text-sm font-semibold break-words mb-0.5">{p.caption}</div>}
                      <div className="text-xs text-muted-foreground truncate">{p.author_name} · {formatDate(p.date)}</div>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <span className="text-[11px] font-bold text-muted-foreground bg-card border border-border rounded-full px-2 py-0.5 truncate">
                          {p.source_label}
                        </span>
                        <div className="flex-1" />
                        {canEdit && (
                          <>
                            <button type="button" onClick={() => setCaption({ id: p.id, text: p.caption || "" })}
                              title="Popisek" className="w-9 h-9 grid place-items-center text-muted-foreground hover:text-brand shrink-0"
                              data-testid={`photo-caption-${p.id}`}><Pencil size={14} /></button>
                            {canDelete && (
                              <button type="button" onClick={() => remove(p)} title="Smazat fotku"
                                className="w-9 h-9 grid place-items-center text-muted-foreground hover:text-destructive shrink-0"
                                data-testid={`photo-del-${p.id}`}><Trash2 size={14} /></button>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <LoadMore hasMore={!!data.next_cursor} loading={loadingMore} onMore={loadMore}
        shown={photos.length} total={data.total} testid="photos-more" />

      {/* 185: jednotný prohlížeč fotek */}
      <PhotoPinViewer open={lightbox != null} onClose={() => setLightbox(null)} photos={photos.map((p) => p.url)} start={lightbox || 0}
        info={(url) => photoInfo(photos.find((p) => p.url === url))}
        onEditCaption={canEdit ? (url) => { const p = photos.find((x) => x.url === url); if (p) { setLightbox(null); setCaption({ id: p.id, text: p.caption || "" }); } } : undefined}
        onDelete={canEdit ? async (url) => {
          const p = photos.find((x) => x.url === url); if (!p) return;
          setLightbox(null);
          try { await api.delete(`/photos/${p.id}`); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        } : undefined} />
    </div>
  );
}
